'use client'

import React, { createContext, useContext, useState, useEffect } from 'react'
import { useRouter } from 'next/navigation'
import { CustomerProfile } from '../types'
import { getCustomerProfile } from '../api/profile'
import { clearAuthToken, getAuthToken, getCachedProfile, onAuthChange, emitProfileChange } from '@/lib/auth-token'

interface CustomerContextType {
  profile: CustomerProfile | null
  isLoading: boolean
  error: string | null
  refreshProfile: () => Promise<void>
  setProfile: React.Dispatch<React.SetStateAction<CustomerProfile | null>>
}

const CustomerContext = createContext<CustomerContextType | undefined>(undefined)

export function CustomerProvider({ children }: { children: React.ReactNode }) {
  const router = useRouter()

  // Initialize from in-memory cache so profile survives provider remounts
  // (Next.js App Router may remount client components during layout transitions)
  const [profile, setProfile] = useState<CustomerProfile | null>(
    () => (getCachedProfile() as CustomerProfile | null) ?? null
  )
  const [isLoading, setIsLoading] = useState(() => !getCachedProfile())
  const [error, setError] = useState<string | null>(null)

  const fetchProfile = async () => {
    const token = getAuthToken()
    if (!token) {
      setProfile(null)
      setIsLoading(false)
      if (typeof window !== 'undefined' && window.location.pathname.startsWith('/customer')) {
        const returnTo = encodeURIComponent(window.location.pathname + window.location.search)
        router.replace(`/login?returnTo=${returnTo}`)
      }
      return
    }

    if (!profile && !getCachedProfile()) {
      setIsLoading(true)
    }
    setError(null)
    try {
      const data = await getCustomerProfile()
      setProfile(data)
    } catch (err: unknown) {
      const e = err as { status?: number; message?: string }
      if (e.status === 401) {
        clearAuthToken()
        setProfile(null)
        if (typeof window !== 'undefined' && window.location.pathname.startsWith('/customer')) {
          const returnTo = encodeURIComponent(window.location.pathname + window.location.search)
          router.replace(`/login?returnTo=${returnTo}`)
        }
      } else {
        setError(e.message || 'Failed to load profile.')
      }
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    void fetchProfile()

    const unsubscribe = onAuthChange((loggedIn) => {
      if (loggedIn) {
        void fetchProfile()
      } else {
        setProfile(null)
        setIsLoading(false)
        if (typeof window !== 'undefined' && window.location.pathname.startsWith('/customer')) {
          const returnTo = encodeURIComponent(window.location.pathname + window.location.search)
          router.replace(`/login?returnTo=${returnTo}`)
        }
      }
    })

    return () => {
      unsubscribe()
    }
  }, [])

  useEffect(() => {
    if (profile !== null) {
      emitProfileChange(profile)
    }
  }, [profile])

  return (
    <CustomerContext.Provider
      value={{
        profile,
        isLoading,
        error,
        refreshProfile: fetchProfile,
        setProfile,
      }}
    >
      {children}
    </CustomerContext.Provider>
  )
}

export function useCustomerContext() {
  const context = useContext(CustomerContext)
  if (!context) {
    return {
      profile: null,
      isLoading: false,
      error: null,
      refreshProfile: async () => {},
      setProfile: (() => {}) as React.Dispatch<React.SetStateAction<CustomerProfile | null>>,
    }
  }
  return context
}

